// src/pages/ja/EXIFPage_ja.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { parseNovelAIMetadata } from "../EXIF/novelaiMeta";
import "../EXIF/EXIFPage.css";

function EXIFPage() {
  const [darkMode, setDarkMode] = useState(false);

  // モバイル判定
  const [isMobile, setIsMobile] = useState(false);

  // 左パネル状態
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewHeight, setPreviewHeight] = useState(400);

  // タブ状態: 初期値 "NovelAI"
  const [activeTab, setActiveTab] = useState("NovelAI");

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const EMPTY_TEXT = "情報が見つかりません。";

  // NAI: 各フィールドの出力状態
  const [naiPositiveDump, setNaiPositiveDump] = useState(EMPTY_TEXT);
  const [naiCharPositiveDump, setNaiCharPositiveDump] = useState(EMPTY_TEXT);
  const [naiNegativeDump, setNaiNegativeDump] = useState(EMPTY_TEXT);
  const [naiCharNegativeDump, setNaiCharNegativeDump] = useState(EMPTY_TEXT);
  const [naiModelDump, setNaiModelDump] = useState(EMPTY_TEXT);
  const [naiStealthDump, setNaiStealthDump] = useState(EMPTY_TEXT);

  // 現在ページの canonical URL (CSR 基準)
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI 画像 EXIF 抽出ツール",
      description:
        "PNG・WebP 画像の EXIF・テキストメタデータを無料で抽出する TooLoves の AI 画像 EXIF 抽出ツール",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "PNG, WebP, 無料, EXIF 抽出, メタデータ, NovelAI, ステルス PNG, プロンプト抽出, TooLoves",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "JPY",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // ブラウザ/モバイルのナビゲーションバー色
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const el = document.createElement("meta");
      el.name = "theme-color";
      el.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(el);
    }
  }, [darkMode]);

  // モバイル環境の検出: UA + matchMedia(pointer, width)
  useEffect(() => {
    const getIsMobile = () => {
      if (typeof window === "undefined") return false;
      const ua = navigator.userAgent || navigator.vendor || "";
      const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      const supportsMatch = typeof window.matchMedia === "function";
      const coarse = supportsMatch ? window.matchMedia("(pointer: coarse)").matches : false;
      const maxWidth = supportsMatch ? window.matchMedia("(max-width: 820px)").matches : false;

      return uaMobile || (coarse && maxWidth);
    };

    const apply = () => setIsMobile(getIsMobile());
    apply();

    const onChange = () => apply();

    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    const mqlCoarse = typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)") : null;
    const mqlMax = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 820px)") : null;

    mqlCoarse && mqlCoarse.addEventListener && mqlCoarse.addEventListener("change", onChange);
    mqlMax && mqlMax.addEventListener && mqlMax.addEventListener("change", onChange);

    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
      mqlCoarse && mqlCoarse.removeEventListener && mqlCoarse.removeEventListener("change", onChange);
      mqlMax && mqlMax.removeEventListener && mqlMax.removeEventListener("change", onChange);
    };
  }, []);

  // ファイルアップロード処理(UIのみ)
  const addFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    const imgs = arr.filter((f) => f.type.startsWith("image/"));
    const withId = imgs.map((f) => ({
      id: `${Date.now()}_${Math.random()}`,
      file: f,
      url: URL.createObjectURL(f),
    }));
    setFiles((prev) => {
      const merged = [...prev, ...withId];
      return merged;
    });
    if (withId.length > 0) setSelectedId(withId[withId.length - 1].id);
  }, []);

  const onPickClick = useCallback(() => fileInputRef.current?.click(), []);
  const onFileChange = useCallback(
    (e) => {
      if (e.target.files?.length) addFiles(e.target.files);
      // 同一ファイルの再アップロードを許可
      e.target.value = "";
    },
    [addFiles]
  );

  const onRemove = useCallback((id) => {
    setFiles((prev) => prev.filter((x) => x.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const onRemoveDuplicates = useCallback(() => {
    setFiles((prev) => {
      const seen = new Set();
      const deduped = [];
      for (let i = prev.length - 1; i >= 0; i--) {
        const name = prev[i].file.name;
        if (!seen.has(name)) {
          seen.add(name);
          deduped.unshift(prev[i]);
        }
      }
      return deduped;
    });
  }, []);

  const onReset = useCallback(() => {
    setFiles((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setSelectedId(null);
    setNaiPositiveDump(EMPTY_TEXT);
    setNaiCharPositiveDump(EMPTY_TEXT);
    setNaiNegativeDump(EMPTY_TEXT);
    setNaiCharNegativeDump(EMPTY_TEXT);
    setNaiModelDump(EMPTY_TEXT);
    setNaiStealthDump(EMPTY_TEXT);
  }, []);

  const selected = useMemo(
    () => files.find((f) => f.id === selectedId) || null,
    [files, selectedId]
  );

  // 安全な JSON パーサ
  const tryParseJSON = useCallback((text) => {
    if (!text || typeof text !== "string") return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }, []);

  // Stealth 元から値を抽出
  const extractFromStealth = useCallback(
    (out) => {
      const raw = out?.stealth?.raw || "";
      const rootObj = tryParseJSON(raw);
      const commentStr =
        (rootObj && typeof rootObj.Comment === "string" && rootObj.Comment) || "";
      const commentObj = tryParseJSON(commentStr) || {};

      // ベース/キャラクタープロンプト
      const pos = (commentObj.prompt || "").trim();
      const charPos = (commentObj?.v4_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // ネガティブプロンプト
      const neg =
        (commentObj?.v4_negative_prompt?.caption?.base_caption || "").trim() ||
        (commentObj.uc || "").trim();

      const charNeg = (commentObj?.v4_negative_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // モデル/設定
      const steps = commentObj?.steps;
      const sampler = commentObj?.sampler;
      const noise = commentObj?.noise_schedule;
      const seed = commentObj?.seed;
      const width = commentObj?.width;
      const height = commentObj?.height;
      const scale = commentObj?.scale;
      const cfgRescale = commentObj?.cfg_rescale;

      // Source 優先順位
      const source =
        rootObj?.Source ||
        out?.pngTexts?.texts?.Source ||
        out?.webpMeta?.customTexts?.Source ||
        "";

      const modelLines = [];
      if (steps != null) modelLines.push(`Steps: ${steps}`);
      if (sampler != null) modelLines.push(`Sampler: ${sampler}`);
      if (noise != null) modelLines.push(`Noise Schedule: ${noise}`);
      if (seed != null) modelLines.push(`Seed: ${seed}`);
      if (width != null && height != null) modelLines.push(`Size: ${width} x ${height}`);
      if (scale != null) modelLines.push(`Scale: ${scale}`);
      if (cfgRescale != null) modelLines.push(`CFG Rescale: ${cfgRescale}`);
      if (source) modelLines.push(`Source: ${source}`);

      const prettyStealth = rootObj ? JSON.stringify(rootObj, null, 2) : raw || "";

      return {
        pos,
        charPos,
        neg,
        charNeg,
        modelDump: modelLines.join("\n"),
        stealthPretty: prettyStealth,
      };
    },
    [tryParseJSON]
  );

  // DnD
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDragOver = (e) => {
      e.preventDefault();
      el.classList.add("dragover");
    };
    const onDragLeave = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
    };
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  // novelaiMeta.js 連携
  useEffect(() => {
    let cancelled = false;
    async function run() {
      // 初期化
      setNaiPositiveDump(EMPTY_TEXT);
      setNaiCharPositiveDump(EMPTY_TEXT);
      setNaiNegativeDump(EMPTY_TEXT);
      setNaiCharNegativeDump(EMPTY_TEXT);
      setNaiModelDump(EMPTY_TEXT);
      setNaiStealthDump(EMPTY_TEXT);

      if (!selected?.file) return;

      try {
        const file = selected.file;
        const ab = await file.arrayBuffer();
        const out = await parseNovelAIMetadata({
          arrayBuffer: ab,
          mime: file.type,
          imageSource: file, // stealth_pngcomp 抽出に使用
        });

        // 1次: Stealth 元から直接抽出
        const fromStealth = extractFromStealth(out);

        // 2次: フォールバック (既存パーサ結果)
        const fallbackPos = (out?.result?.sampler?.prompts || []).join("\n").trim();
        const fallbackNeg = (out?.result?.sampler?.negative_prompts || [])
          .join("\n")
          .trim();

        const pos = fromStealth.pos || fallbackPos;
        const neg = fromStealth.neg || fallbackNeg;

        if (!cancelled) {
          setNaiPositiveDump(pos || EMPTY_TEXT);
          setNaiCharPositiveDump(fromStealth.charPos || EMPTY_TEXT);
          setNaiNegativeDump(neg || EMPTY_TEXT);
          setNaiCharNegativeDump(fromStealth.charNeg || EMPTY_TEXT);
          setNaiModelDump(fromStealth.modelDump || EMPTY_TEXT);
          setNaiStealthDump(fromStealth.stealthPretty || EMPTY_TEXT);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = `解析エラー: ${e?.message || String(e)}`;
          setNaiPositiveDump(msg);
          setNaiNegativeDump(EMPTY_TEXT);
          setNaiCharPositiveDump(EMPTY_TEXT);
          setNaiCharNegativeDump(EMPTY_TEXT);
          setNaiModelDump(EMPTY_TEXT);
          setNaiStealthDump(EMPTY_TEXT);
        }
      }
    }
    run();
    return () => {
      let _ = cancelled; // silence eslint
      cancelled = true;
    };
  }, [selected, extractFromStealth]);

  // 共通コピー ヘルパー
  const copyText = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch {}
  }, []);

  const isEmpty = (v) => !v || v.trim() === "" || v === EMPTY_TEXT;

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ja" }}>
        <title>AI 画像 EXIF 抽出ツール | TooLoves</title>
        <meta
          name="description"
          content="PNG・WebP 画像の EXIF・テキストメタデータを無料で抽出する AI 画像 EXIF 抽出ツール。NovelAI ステルス PNG、プロンプト、シード、モデルなどに対応。"
        />
        <meta
          name="keywords"
          content="PNG, WebP, 無料, EXIF 抽出, メタデータ, 画像メタデータ, NovelAI, ステルス PNG, プロンプト抽出, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 初期 theme-color。ランタイムで useEffect にて同期 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="AI 画像 EXIF 抽出ツール | TooLoves" />
        <meta
          property="og:description"
          content="PNG・WebP 画像の EXIF・テキストメタデータを無料で抽出。"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-exif.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI 画像 EXIF 抽出ツール | TooLoves" />
        <meta
          name="twitter:description"
          content="PNG・WebP 画像の EXIF・テキストメタデータを無料で抽出。"
        />
        <meta name="twitter:image" content="/og-exif.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/ja" className="brand-link" aria-label="damoa 日本語トップへ移動">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode((v) => !v)}
            aria-label="テーマ切り替え"
          >
            {darkMode ? "ライトモード" : "ダークモード"}
          </button>
        </div>
      </header>

      <main className="container exif-container">
        {isMobile ? (
          // モバイル案内専用ブロック
          <section className="generator-card" role="region" aria-label="モバイル対応のご案内">
            <div className="metadata-section">
              <h3 className="section-title">モバイル対応のご案内</h3>
              <p className="hint">この機能はモバイルではご利用いただけません。</p>
              <div className="terms-actions">
                <Link to="/ja" className="btn" aria-label="メインページへ戻る">
                  メインへ戻る
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* 左パネル */}
            <section className="generator-card panel-left">
              <div className="upload-header">
                <h4 className="section-title">画像のアップロード</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={onFileChange}
                />
                <button
                  type="button"
                  onClick={onPickClick}
                  aria-label="画像ファイルを選択"
                  className="btn btn-primary"
                >
                  ファイルを選択
                </button>
              </div>

              <div ref={dropRef} id="drop-zone">
                ここに画像をドラッグ＆ドロップするか、「ファイルを選択」ボタンを使用してください
              </div>

              <div className="preview-header">
                <h2 className="section-title small">画像プレビュー</h2>
                <div className="preview-size-control">
                  <input
                    type="range"
                    min={100}
                    max={1200}
                    value={previewHeight}
                    onChange={(e) => setPreviewHeight(parseInt(e.target.value, 10))}
                  />
                  <span id="preview-size-value">{previewHeight}px</span>
                </div>
              </div>

              <div className="qr-wrap">
                {selected?.url ? (
                  <img
                    className="preview-img"
                    src={selected.url}
                    alt="画像プレビュー"
                    height={previewHeight}
                  />
                ) : (
                  <p className="placeholder">選択された画像がありません</p>
                )}
              </div>

              <div className="actions">
                <button onClick={onRemoveDuplicates}>重複を削除</button>
                <button onClick={onReset}>リセット</button>
              </div>

              <h2 className="section-title small list-title">画像リスト</h2>
              <ul id="image-list">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className={`file-row ${f.id === selectedId ? "selected" : ""}`}
                    onClick={() => setSelectedId(f.id)}
                  >
                    <span className="file-name">{f.file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(f.id);
                      }}
                      className="btn btn-secondary"
                    >
                      削除
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {/* 右パネル */}
            <section className="generator-card panel-right">
              {/* タブ */}
              <ul className="tabs">
                {["NovelAI"].map((label) => {
                  const key = label;
                  const active = activeTab === key;
                  return (
                    <li
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={active ? "active" : ""}
                    >
                      {label}
                    </li>
                  );
                })}
              </ul>

              {/* NovelAI タブ */}
              <div className={`tab-content ${activeTab === "NovelAI" ? "active" : ""}`}>
                <div className="metadata-section">
                  {/* ポジティブプロンプト */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-positive" className="label-strong">
                        ポジティブプロンプト
                      </label>
                      <button
                        onClick={() => copyText(naiPositiveDump)}
                        aria-label="ポジティブプロンプトをコピー"
                        disabled={isEmpty(naiPositiveDump)}
                        className="btn btn-primary"
                      >
                        コピー
                      </button>
                    </div>
                    <div
                      id="nai-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiPositiveDump}
                    </div>
                  </div>

                  {/* キャラクターポジティブプロンプト */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-positive" className="label-strong">
                        キャラクターポジティブプロンプト
                      </label>
                      <button
                        onClick={() => copyText(naiCharPositiveDump)}
                        aria-label="キャラクターポジティブプロンプトをコピー"
                        disabled={isEmpty(naiCharPositiveDump)}
                        className="btn btn-primary"
                      >
                        コピー
                      </button>
                    </div>
                    <div
                      id="nai-char-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharPositiveDump}
                    </div>
                  </div>

                  {/* ネガティブプロンプト */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-negative" className="label-strong">
                        ネガティブプロンプト
                      </label>
                      <button
                        onClick={() => copyText(naiNegativeDump)}
                        aria-label="ネガティブプロンプトをコピー"
                        disabled={isEmpty(naiNegativeDump)}
                        className="btn btn-primary"
                      >
                        コピー
                      </button>
                    </div>
                    <div
                      id="nai-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiNegativeDump}
                    </div>
                  </div>

                  {/* キャラクターネガティブプロンプト */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-negative" className="label-strong">
                        キャラクターネガティブプロンプト
                      </label>
                      <button
                        onClick={() => copyText(naiCharNegativeDump)}
                        aria-label="キャラクターネガティブプロンプトをコピー"
                        disabled={isEmpty(naiCharNegativeDump)}
                        className="btn btn-primary"
                      >
                        コピー
                      </button>
                    </div>
                    <div
                      id="nai-char-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharNegativeDump}
                    </div>
                  </div>

                  {/* NAI モデルと設定 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-model" className="label-strong">
                        NAI モデルと設定
                      </label>
                      <button
                        onClick={() => copyText(naiModelDump)}
                        aria-label="NAI モデルと設定をコピー"
                        disabled={isEmpty(naiModelDump)}
                        className="btn btn-primary"
                      >
                        コピー
                      </button>
                    </div>
                    <div
                      id="nai-model"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiModelDump}
                    </div>
                  </div>

                  {/* Stealth PNG メタデータ */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-stealth" className="label-strong">
                        Stealth PNG メタデータ
                      </label>
                      <button
                        onClick={() => copyText(naiStealthDump)}
                        aria-label="Stealth PNG メタデータをコピー"
                        disabled={isEmpty(naiStealthDump)}
                        className="btn btn-primary"
                      >
                        コピー
                      </button>
                    </div>
                    <div
                      id="nai-stealth"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiStealthDump}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法的情報リンク">
          <Link to="/terms" className="footer-link">
            利用規約
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/privacy" className="footer-link">
            プライバシーポリシー
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default EXIFPage;